import type { CompanyAsset, MagicSlidePayload, Slide } from '../types/slide';

export type Visibility = 'private' | 'company' | 'unlisted';

export interface DeckVersion {
  id: string;
  title: string;
  created_at: string;
  created_by: string | null;
  author_name: string | null;
  slide_count: number | null;
}

export interface DeckComment {
  id: string;
  body: string;
  author_email: string;
  author_name: string;
  created_at: string;
  resolved: boolean;
  resolved_by: string | null;
  resolved_at: string | null;
  mentions: string[];
}

export interface DirectoryPerson {
  email: string;
  name: string;
}

export interface TemplateSummary {
  id: string;
  title: string;
  description: string;
  theme: string;
  created_by: string;
  author_name: string;
  created_at: string;
  updated_at: string;
  slide_count: number | null;
}

export type SlideAiAction = 'tighten' | 'add_kpi' | 'notes_to_bullets';

export interface DeckSummary {
  id: string;
  title: string;
  owner_email: string;
  owner_name: string;
  theme: string;
  visibility: Visibility;
  category: string;
  created_at: string;
  updated_at: string;
  slide_count: number | null;
  /** First slide, for thumbnails. */
  first_slide: Slide | null;
  /** Your relationship to the deck in "mine" lists: owner or invited co-editor. */
  my_role?: 'owner' | 'editor' | 'viewer';
}

export interface DeckDetail {
  id: string;
  title: string;
  ownerEmail: string;
  ownerName: string;
  slides: Slide[];
  theme: string;
  visibility: Visibility;
  category: string;
  canEdit: boolean;
  /** Owner (can share and manage editors), as opposed to a co-editor. */
  isOwner: boolean;
  updatedAt: string;
}

export interface DeckEditor {
  email: string;
  name: string;
  created_at: string;
}

export interface PortalDocument {
  id: string;
  title: string;
  description: string;
  category: string;
  file_name: string;
  content_type: string;
  size: number;
  created_at: string;
  updated_at?: string;
  uploaded_by_name?: string;
}

export interface AppNotification {
  type: 'mention' | 'announcement' | 'shared' | 'editor';
  id: string;
  at: string;
  deck_id: string | null;
  title: string;
  body: string;
  unread: boolean;
}

export interface AnnouncementReader {
  id: string;
  email: string;
  name: string;
  department_id: string | null;
  read_at: string | null;
}

export interface Announcement {
  id: string;
  title: string;
  body: string;
  category: string;
  deck_id: string | null;
  deck_title: string | null;
  deck_first_slide: Slide | null;
  pinned: boolean;
  author_name: string;
  created_at: string;
  updated_at: string;
  require_ack: boolean;
  publish_at: string | null;
  expires_at: string | null;
  attachment_id: string | null;
  attachment_title: string | null;
  attachment_file_name: string | null;
  attachment_size: number | null;
  my_read_at: string | null;
  read_count: number;
  /** Admins also receive scheduled and expired announcements. */
  status: 'live' | 'scheduled' | 'expired';
}

export interface Department {
  id: string;
  name: string;
  description: string;
  color: string;
  sort_order: number;
  deck_count: number;
  announcement_count: number;
  last_updated: string | null;
}

export interface Mention {
  id: string;
  body: string;
  created_at: string;
  deck_id: string;
  deck_title: string;
  author_name: string;
  resolved: boolean;
}

export interface DepartmentInput {
  name: string;
  description: string;
  color: string;
}

export interface AnnouncementInput {
  requireAck?: boolean;
  publishAt?: string | null;
  expiresAt?: string | null;
  attachmentId?: string | null;
  title: string;
  body: string;
  category: string;
  deckId: string | null;
  pinned: boolean;
}

export type Role = 'admin' | 'member';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  mustChangePassword: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  active: boolean;
  mustChangePassword: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  departmentId: string | null;
}

export interface AppSettings {
  aiEnabled: boolean;
  aiModel: string;
  aiDailyLimit: number;
}

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** Fired when the server says the session is gone, so the app can show the sign-in screen. */
export const UNAUTHORIZED_EVENT = 'slidesprint:unauthorized';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/api/auth/')) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    throw new ApiError((data as { error?: string }).error || `HTTP ${res.status}`, res.status);
  }
  return data as T;
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: body === undefined ? undefined : JSON.stringify(body),
});

export const api = {
  me: () => request<{ user: SessionUser }>('/api/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: SessionUser }>('/api/auth/login', json('POST', { email, password })),
  logout: () => request<{ ok: boolean }>('/api/auth/logout', json('POST')),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ user: SessionUser }>('/api/auth/change-password', json('POST', { currentPassword, newPassword })),
  publicSettings: () => request<{ aiEnabled: boolean }>('/api/settings'),

  admin: {
    listUsers: () => request<{ users: AdminUser[] }>('/api/admin/users'),
    createUser: (payload: { email: string; name: string; role: Role; departmentId?: string | null }) =>
      request<{ user: AdminUser; tempPassword: string }>('/api/admin/users', json('POST', payload)),
    updateUser: (id: string, patch: Partial<Pick<AdminUser, 'name' | 'role' | 'active' | 'departmentId'>>) =>
      request<{ user: AdminUser }>(`/api/admin/users/${id}`, json('PATCH', patch)),
    resetPassword: (id: string) =>
      request<{ tempPassword: string }>(`/api/admin/users/${id}/reset-password`, json('POST')),
    deleteUser: (id: string) => request<{ ok: boolean }>(`/api/admin/users/${id}`, json('DELETE')),
    getSettings: () =>
      request<{ settings: AppSettings; models: { id: string; name: string }[] }>('/api/admin/settings'),
    saveSettings: (patch: Partial<AppSettings>) =>
      request<{ settings: AppSettings; models: { id: string; name: string }[] }>(
        '/api/admin/settings',
        json('PUT', patch),
      ),
    usage: () => request<{ day: string; usage: Record<string, number> }>('/api/admin/usage'),
    createDepartment: (input: DepartmentInput) => request<{ id: string }>('/api/admin/departments', json('POST', input)),
    updateDepartment: (id: string, input: DepartmentInput) =>
      request<{ ok: boolean }>(`/api/admin/departments/${id}`, json('PUT', input)),
    deleteDepartment: (id: string) => request<{ ok: boolean }>(`/api/admin/departments/${id}`, json('DELETE')),
  },

  listDecks: (scope: 'mine' | 'shared' = 'mine') =>
    request<{ decks: DeckSummary[] }>(`/api/decks?scope=${scope}`),

  deleteDeck: (id: string) => request<{ ok: boolean }>(`/api/decks/${id}`, { method: 'DELETE' }),

  getDeck: (id: string) => request<DeckDetail>(`/api/decks/${id}`),

  createDeck: (payload: { title: string; slides: Slide[]; theme?: string; visibility?: Visibility; category?: string }) =>
    request<{ id: string; updatedAt: string }>('/api/decks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  saveDeck: (
    id: string,
    payload: {
      title: string;
      slides: Slide[];
      theme?: string;
      visibility?: Visibility;
      category?: string;
      expectedUpdatedAt?: string | null;
      saveVersion?: boolean;
    },
  ) =>
    request<{ id: string; updatedAt: string; saveStatus: string }>(`/api/decks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),

  listVersions: (id: string) => request<{ versions: DeckVersion[] }>(`/api/decks/${id}/versions`),

  restoreVersion: (id: string, versionId: string) =>
    request<{ id: string; title: string; slides: Slide[]; updatedAt: string }>(
      `/api/decks/${id}/versions/${versionId}/restore`,
      json('POST'),
    ),

  listComments: (id: string) => request<{ comments: DeckComment[] }>(`/api/decks/${id}/comments`),

  addComment: (id: string, body: string, mentions: string[] = []) =>
    request<{ comment: DeckComment }>(`/api/decks/${id}/comments`, json('POST', { body, mentions })),

  resolveComment: (deckId: string, commentId: string, resolved: boolean) =>
    request<{ comment: DeckComment }>(`/api/decks/${deckId}/comments/${commentId}`, json('PATCH', { resolved })),

  deleteComment: (deckId: string, commentId: string) =>
    request<{ ok: boolean }>(`/api/decks/${deckId}/comments/${commentId}`, { method: 'DELETE' }),

  directory: () => request<{ people: DirectoryPerson[] }>('/api/directory'),

  listTemplates: () => request<{ templates: TemplateSummary[] }>('/api/templates'),

  getTemplate: (id: string) =>
    request<{
      id: string;
      title: string;
      description: string;
      theme: string;
      slides: Slide[];
      builtin: boolean;
    }>(`/api/templates/${id}`),

  createTemplate: (payload: {
    title: string;
    description?: string;
    theme?: string;
    slides?: Slide[];
    deckId?: string;
  }) => request<{ id: string }>('/api/templates', json('POST', payload)),

  deleteTemplate: (id: string) => request<{ ok: boolean }>(`/api/templates/${id}`, { method: 'DELETE' }),

  departments: () => request<{ departments: Department[] }>('/api/departments'),

  mentions: () => request<{ mentions: Mention[] }>('/api/mentions'),

  announcements: {
    list: () => request<{ announcements: Announcement[] }>('/api/announcements'),
    create: (input: AnnouncementInput) => request<{ id: string }>('/api/announcements', json('POST', input)),
    update: (id: string, input: AnnouncementInput) =>
      request<{ ok: boolean }>(`/api/announcements/${id}`, json('PUT', input)),
    remove: (id: string) => request<{ ok: boolean }>(`/api/announcements/${id}`, json('DELETE')),
    markRead: (id: string) => request<{ readAt: string }>(`/api/announcements/${id}/read`, json('POST')),
    reads: (id: string) =>
      request<{ title: string; people: AnnouncementReader[] }>(`/api/announcements/${id}/reads`),
  },

  documents: {
    list: (category?: string) =>
      request<{ documents: PortalDocument[]; storageEnabled: boolean }>(
        category ? `/api/documents?category=${encodeURIComponent(category)}` : '/api/documents',
      ),
    upload: (file: File, meta: { title: string; description: string; category: string }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('title', meta.title);
      form.append('description', meta.description);
      form.append('category', meta.category);
      return request<{ id: string }>('/api/documents', { method: 'POST', body: form });
    },
    update: (id: string, meta: { title: string; description: string; category: string }) =>
      request<{ ok: boolean }>(`/api/documents/${id}`, json('PUT', meta)),
    remove: (id: string) => request<{ ok: boolean }>(`/api/documents/${id}`, json('DELETE')),
    fileUrl: (id: string) => `/api/documents/${id}/file`,
  },

  search: (q: string) =>
    request<{ decks: DeckSummary[]; announcements: Pick<Announcement, 'id' | 'title' | 'body' | 'category' | 'created_at'>[]; documents: PortalDocument[] }>(
      `/api/search?q=${encodeURIComponent(q)}`,
    ),

  notifications: () => request<{ notifications: AppNotification[]; unread: number }>('/api/notifications'),
  markNotificationsSeen: () => request<{ ok: boolean }>('/api/notifications/seen', json('POST')),

  deckEditors: (deckId: string) => request<{ editors: DeckEditor[] }>(`/api/decks/${deckId}/editors`),
  setDeckEditors: (deckId: string, emails: string[]) =>
    request<{ ok: boolean; editors: string[] }>(`/api/decks/${deckId}/editors`, json('PUT', { emails })),

  deleteAsset: (id: string) => request<{ ok: boolean }>(`/api/assets/${id}`, json('DELETE')),

  listAssets: (category?: string) =>
    request<{ assets: CompanyAsset[]; storageEnabled: boolean }>(
      category ? `/api/assets?category=${encodeURIComponent(category)}` : '/api/assets',
    ),

  uploadAsset: async (file: File, category = 'general') => {
    const form = new FormData();
    form.append('file', file);
    form.append('name', file.name);
    form.append('category', category);
    return request<{ id: string; r2Url: string; name: string }>('/api/assets/upload', {
      method: 'POST',
      body: form,
    });
  },

  /** Personal slide image (any signed-in user). Not added to the company library. */
  uploadSlideMedia: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ id: string; r2Url: string; name: string }>('/api/media/upload', {
      method: 'POST',
      body: form,
    });
  },

  searchUnsplash: (q: string, page = 1) =>
    request<{
      total: number;
      photos: {
        id: string;
        alt: string;
        thumbUrl: string;
        imageUrl: string;
        photographer: string;
        photographerUrl: string;
        unsplashUrl: string;
        downloadLocation: string;
        color: string;
      }[];
    }>(`/api/unsplash/search?q=${encodeURIComponent(q)}&page=${page}`),

  insertUnsplash: (photo: {
    downloadLocation: string;
    imageUrl: string;
    alt?: string;
    photographer?: string;
  }) =>
    request<{ imageUrl: string; alt: string; photographer: string }>('/api/unsplash/insert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(photo),
    }),

  magicDraft: (notes: string, deckTitle?: string) =>
    request<{ title: string; slides: MagicSlidePayload[]; model: string }>(
      '/api/ai/groq-generate-deck',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes, deckTitle }),
      },
    ),

  polishText: (
    text: string,
    action: 'shorten' | 'bullets' | 'action_title' | 'grammar' | 'executive',
  ) =>
    request<{ text: string; model: string }>('/api/ai/groq-polish-text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, action }),
    }),

  editSlide: (
    action: SlideAiAction,
    input: { layoutId: string; notes?: string; payload: Record<string, unknown> },
  ) =>
    request<{ action: SlideAiAction; result: Record<string, unknown>; model: string }>(
      '/api/ai/groq-edit-slide',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...input }),
      },
    ),
};
